import { BookingSteps, UserProfile } from 'fitbeat.gym-core';
import React, { FunctionComponent } from 'react';
import { translate } from 'react-i18next';
import { Image, SafeAreaView, StyleProp, StyleSheet, Text, TextStyle, View, ViewStyle } from 'react-native';
import FastImage from 'react-native-fast-image';
import { connect } from 'react-redux';
import { Button, ButtonSize, ButtonTypes } from '../../components';
import { HeartRateFaq, IAppStore } from '../../HomeWorkouts';
import Theme, { ThemeType } from '../../theme';
import { createBookingServiceClient } from '../../utils/apiClientFactory';
import { defaultScale } from '../../utils/scaling';
import {
  closeFeatureDemoBookingScreen,
  setupHRMonitorNextPressed,
  setupHRMonitorViewSupportedDevicesClosed,
  setupHRMonitorViewSupportedDevicesPressed,
} from '../actions';
import { HeaderWithCloseButton } from '../components/HeaderWithCloseButton';

interface IProps {
  t: (key: string, options?: any) => string;
  closePressed: () => void;
  closeModal: () => void;
  userProfile: UserProfile;
  next: () => void;
  viewSupportedDevices: () => void;
  closeSupportedDevices: () => void;
  showSupportedDevices: boolean;
}

interface IThemeStyle {
  safeArea: StyleProp<ViewStyle>;
}

const themeStyleMapper: { [key in ThemeType]: IThemeStyle } = {
  [ThemeType.light]: {
    safeArea: {
      flex: 1,
    },
  },
  [ThemeType.dark]: {
    safeArea: {
      flex: 1,
      backgroundColor: Theme.BLACK,
    },
  },
};

export const SetupHRMonitor: FunctionComponent<IProps> = ({
                                                            t,
                                                            closeModal,
                                                            closePressed,
                                                            next,
                                                            viewSupportedDevices,
                                                            closeSupportedDevices,
                                                            showSupportedDevices,
                                                          }) => {

  const close = () => {
    if (showSupportedDevices) {
      return closeSupportedDevices();
    }
    closePressed();
    closeModal();
  };

  const themeType = showSupportedDevices ? ThemeType.dark : ThemeType.light;

  const theme = themeStyleMapper[themeType];

  return (
    <SafeAreaView style={theme.safeArea}>
      {
        showSupportedDevices &&
        <View style={{ flex: 1 }}>
          <HeaderWithCloseButton onClose={close} containerStyle={{ paddingHorizontal: 15, paddingTop: 15 }} />
          <HeartRateFaq closeModal={closeSupportedDevices} />
        </View>
      }
      {
        !showSupportedDevices &&
        <View style={styles.container}>
          <HeaderWithCloseButton onClose={close} />
          <View style={styles.imageContainer}>
            <FastImage
              style={styles.image}
              resizeMode={FastImage.resizeMode.contain}
              source={Image.resolveAssetSource(require('../../assets/images/hr-mon-next-to-phone.png'))}
            />
          </View>
          <View style={styles.textContainer}>
            <Text style={styles.heading}>{t('tracking:heartRateMonitor')}</Text>
            <Text style={styles.body}>{t('wearYourHRMonitor')}</Text>
          </View>
          <View>
            <Button
              fontColor={Theme.PRIMARY}
              containerStyle={{ marginBottom: defaultScale(15) }}
              size={ButtonSize.MEDIUM}
              buttonType={ButtonTypes.TRANSPARENT}
              title={t('viewSupportedDevices')}
              onPress={viewSupportedDevices}
            />
            <Button
              fontColor={Theme.WHITE}
              size={ButtonSize.MEDIUM}
              title={t('next')}
              onPress={next}
            />
          </View>
        </View>
      }
    </SafeAreaView>
  );
};

const mapStateToProps = (state: IAppStore) => {
  return {
    showSupportedDevices: state.bookings.bookingWizard.showSupportedDevices,
  };
};

const mapDispatchToProps = (dispatch, ownProps: IProps): Partial<IProps> => {
  const { userProfile } = ownProps;
  return {
    next: () => dispatch(setupHRMonitorNextPressed(userProfile)),
    viewSupportedDevices: () => dispatch(setupHRMonitorViewSupportedDevicesPressed()),
    closeSupportedDevices: () => dispatch(setupHRMonitorViewSupportedDevicesClosed()),
    closePressed: () => dispatch(closeFeatureDemoBookingScreen(
      createBookingServiceClient(), BookingSteps.SetupHRMonitor)),
  };
};

export const SetupHRMonitorStep = connect(mapStateToProps, mapDispatchToProps)(
  translate(['bookings'], { wait: true })(SetupHRMonitor));

SetupHRMonitorStep.getTitle = () => <></>;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 15,
  },
  imageContainer: {
    flex: 1,
  },
  textContainer: {
    flex: 0.4,
  },
  heading: {
    ...Theme.textStyle.heading,
    textAlign: 'center',
    marginVertical: defaultScale(10),
  },
  body: {
    ...Theme.textStyle.bodyCopyReg,
    textAlign: 'center',
    marginVertical: defaultScale(10),
  },
  image: {
    height: '100%',
    width: '100%',
    backgroundColor: 'white',
  },
});
